import { useEffect, useState, useRef } from "react";
import { getAddresses, createAddress } from "../../../services/addressService";
import AddressModal from "../../AddressModal";
import "./Step2Address.css";
import {
  MapPin,
  UserRoundCheck,
  Phone,
  MapPinHouse,
  Trash2,
  X,
  MapPinPlus,
} from "lucide-react";

import AddressHeader from "./components/AddressHeader/AddressHeader";
import SelectedAddressCard from "./components/SelectedAddressCard/SelectedAddressCard";
import AddressList from "./components/AddressList/AddressList";
import OrderComment from "./components/OrderComment/OrderComment";


export default function Step2Address({
  onAddressSelected,
  onContinue,
  onBack,
}) {
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [activeAddressId, setActiveAddressId] = useState(null);

  const addressListRef = useRef(null);

  const [addresses, setAddresses] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showCreateAddress, setShowCreateAddress] = useState(false);
  const [comment, setComment] = useState("");


  const [form, setForm] = useState({
    name: "",
    lname: "",
    mobile: "",
    postalCode: "",
    address: "",
    provinceId: "",
    cityId: "",
    status: 1,
  });

  const [highlightList, setHighlightList] = useState(false);

  const loadAddresses = async () => {
    try {
      const data = await getAddresses();

      setAddresses(data);

      if (data.length > 0 && !selectedAddress) {
        setSelectedAddress(data[0]);
        setActiveAddressId(data[0].id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  const handleChangeAddress = () => {
    addressListRef.current?.scrollIntoView({
      behavior: "smooth",

      block: "start",
    });

    setHighlightList(true);

    setTimeout(() => {
      setHighlightList(false);
    }, 1500);
  };

  useEffect(() => {
    if (!addresses.length) return;

    const defaultAddress = addresses[0];

    setSelectedAddress(defaultAddress);

    setActiveAddressId(defaultAddress.id);
  }, [addresses]);

  /*
    |--------------------------------------------------------------------------
    | Load User Addresses
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    const loadAddresses = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getAddresses();

        setAddresses(data);

        /*
                |--------------------------------------------------------------------------
                | If parent already has a selected address,
                | keep it.
                |--------------------------------------------------------------------------
                */

        if (selectedAddress) {
          return;
        }

        /*
                |--------------------------------------------------------------------------
                | Otherwise select active address
                |--------------------------------------------------------------------------
                */

        const activeAddress = data.find(
          (address) => Number(address.status) === 1,
        );

        if (activeAddress) {
          onAddressSelected(activeAddress);
        }
      } catch (error) {
        console.error("Failed to load addresses:", error);

        setError("Unable to load your addresses.");
      } finally {
        setLoading(false);
      }
    };

    loadAddresses();
  }, []);

  /*
    |--------------------------------------------------------------------------
    | Save New Address
    |--------------------------------------------------------------------------
    */

  const handleSaveAddress = async () => {
    try {
      const response = await createAddress(form);

      console.log("response Address:", response);

      /*
            |--------------------------------------------------------------------------
            | Support either:
            |
            | response.address
            | OR
            | response itself
            |--------------------------------------------------------------------------
            */

      const newAddress = response?.addresses ?? response;

      setAddresses((previousAddresses) => [...previousAddresses, newAddress]);

      onAddressSelected(newAddress);

      setShowAddressModal(false);

      setForm({
        name: "",
        lastName: "",
        provinceId: "",
        cityId: "",
        postalCode: "",
        phone: "",
        address: "",
        status: 1,
      });
    } catch (error) {
      console.error("Failed to create address:", error);
    }
  };

    // window.scrollTo({
    //         top: 0,
    //         behavior: "smooth",
    //     });

  /*
    |--------------------------------------------------------------------------
    | Loading State
    |--------------------------------------------------------------------------
    */

  if (loading) {
    return (
      <section className="checkout-step address-step">
        <div className="address-skeleton">
          <div className="skeleton-header" />

          <div className="skeleton-card" />

          <div className="skeleton-card" />
        </div>
      </section>
    );
  }

  /*
    |--------------------------------------------------------------------------
    | Error State
    |--------------------------------------------------------------------------
    */

  if (error) {
    return (
      <section className="checkout-step address-step">
        <div className="address-error">
          <h2>خطایی به وجود آمده</h2>

          <p>{error}</p>

          <button type="button" onClick={onBack}>
            بازگشت
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="checkout-step address-step">
      <AddressHeader />

      <div className="address-layout">
        <div className="address-left-column">
          <SelectedAddressCard
            address={selectedAddress}
            onChange={handleChangeAddress}
            onCreate={() => setShowCreateAddress(true)}
          />

          <OrderComment value={comment} onChange={setComment} />
        </div>

        <div
          ref={addressListRef}
          className={`address-right-column ${highlightList ? "highlight-panel" : ""
            }`}
        >
          <AddressList
            addresses={addresses}
            selectedAddress={selectedAddress}
            onConfirm={(address) => {
              setSelectedAddress(address);
              setActiveAddressId(address.id);
            }}
          />
        </div>
      </div>

      {/* ================================
                ACTIONS
            ================================= */}

      <div className="checkout-step-actions">
        <button
          type="button"
          className="checkout-secondary-btn"
          onClick={onBack}
        >
            <span className="checkout-btn-arrow">                       
                        →
                    </span>
           بازگشت به سبد خرید 
        </button>

        <button
          type="button"
          className="checkout-primary-btn"
          disabled={!selectedAddress}
          onClick={() => {
            if (!selectedAddress) {
              return;
            }

            onContinue({
              address: selectedAddress,
              comment: comment,
            });
          }}
        >
          مرحله بعد
          <span className="checkout-btn-arrow">←</span>
        </button>
      </div>

      {/* ================================
                CREATE ADDRESS MODAL
            ================================= */}

      {showCreateAddress && (
        <AddressModal
          onClose={() => setShowCreateAddress(false)}
          onSuccess={(newAddress) => {
            setAddresses((prev) => [newAddress, ...prev]);
            setSelectedAddress(newAddress);
            setActiveAddressId(newAddress.id);
            setShowCreateAddress(false);
          }}

          form={form}
          setForm={setForm}         
          onSave={() => {
            loadAddresses();
            setShowCreateAddress(false);
          }}
        />
      )}
    </section>
  );
}
